import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { 
  CheckCircle, XCircle, Calendar, Clock, MapPin, Briefcase, 
  Loader2, AlertCircle, PartyPopper, Car, Copy, Check, User, Hash, Phone, FileText, Navigation, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { formatDateWithDay, formatScheduledTime, extractTimeFromDate } from "../utils/dateUtils";
import { getDisplayAddress, getGoogleMapsUrl } from "../utils/job-form-utils";

const API = process.env.REACT_APP_BACKEND_URL;

const JobApproval = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [jobData, setJobData] = useState(null);
  const [responded, setResponded] = useState(false);
  const [responseAction, setResponseAction] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    fetchJobApproval();
  }, [token]);

  const fetchJobApproval = async () => {
    try {
      const res = await axios.get(`${API}/api/job-approval/${token}`);
      
      if (res.data.already_responded) {
        setResponded(true);
        setResponseAction(res.data.status);
        if (res.data.job) setJobData(res.data);
      } else {
        setJobData(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (action) => {
    if (action === "reject" && !showRejectForm) {
      setShowRejectForm(true);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/job-approval/${token}`, {
        token,
        action,
        rejection_reason: action === "reject" ? rejectionReason : null
      });
      
      if (action === "accept") {
        // Re-fetch to get full job details now that it's accepted
        const res = await axios.get(`${API}/api/job-approval/${token}`);
        if (res.data.job) {
          setJobData(res.data);
        }
      }
      
      setResponded(true);
      setResponseAction(action === "accept" ? "accepted" : "rejected");
      toast.success(action === "accept" ? "Job accepted!" : "Job rejected");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond");
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    }).catch(() => toast.error("Failed to copy"));
  };

  const buildTicketText = (job, businessName) => {
    const lines = [];
    lines.push(businessName || "");
    lines.push(`Job ID: ${job.job_display_id || job.job_id || ""}`);
    lines.push(`Name: ${job.customer_name || ""}`);
    // Phone: show masked number with PIN if available, otherwise raw number
    const phone = job.masked_call_number_with_pin || job.customer_phone || "";
    lines.push(`Phone: ${phone}`);
    lines.push(`Address: ${[job.customer_address, job.address_city, job.address_state, job.zip_code].filter(Boolean).join(", ")}`);
    // Date and Time combined
    const datePart = job.scheduled_date ? formatDateWithDay(job.scheduled_date) : "";
    const timePart = formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date) || "";
    lines.push(`Date: ${datePart}${timePart ? " " + timePart : ""}`);
    lines.push(`Job Type: ${job.service_name || ""}`);
    // Vehicle only if present
    const vehicle = [job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ");
    if (vehicle) {
      lines.push(`Vehicle: ${vehicle}`);
    }
    lines.push(`Notes: ${job.notes || ""}`);
    return lines.join("\n");
  };

  // Render a template line, turning phone-like values into tap-to-call links
  const PHONE_RE = /(\(\d{3}\)\s?\d{3}-\d{4}(?:,\d+#)?|\+?\d{10,13}(?:,\d+#)?)/g;
  const PHONE_TEST = /^(\(\d{3}\)\s?\d{3}-\d{4}(?:,\d+#)?|\+?\d{10,13}(?:,\d+#)?)$/;
  const renderTemplateLine = (line, idx) => {
    const parts = line.split(PHONE_RE);
    return (
      <p key={idx} className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
        {parts.map((part, i) =>
          PHONE_TEST.test(part) && part.replace(/\D/g, "").length >= 10 ? (
            <a key={i} href={`tel:${part.replace(/[^0-9,#+]/g, "")}`}
              className="text-green-400 underline decoration-green-400/30">
              {part}
            </a>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </p>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06060b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FFCC00] animate-spin" />
      </div>
    );
  }

  // Already responded - Accepted: show full job details
  if (responded && responseAction === "accepted") {
    const job = jobData?.job;
    const businessName = jobData?.business_name || "";
    return (
      <div className="min-h-screen bg-[#06060b] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Success Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style={{background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)'}}>
              <PartyPopper className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white font-['Manrope']">Job Accepted!</h2>
            <p className="text-white/50 text-sm mt-1">{businessName}</p>
          </div>

          {/* Full Job Details Card */}
          {job && (
            <div className="rounded-2xl overflow-hidden mb-4" style={{background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)'}}>
              {/* Job Title Header */}
              <div className="p-4 border-b border-white/[0.06]">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold text-lg">{job.title}</h3>
                  {job.priority === "high" && (
                    <Badge className="bg-red-500/15 text-red-400 border border-red-500/25 text-xs">Urgent</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <Hash className="w-3.5 h-3.5 text-white/30" />
                  <span className="text-white/40 text-sm font-mono">{job.job_display_id || job.job_id}</span>
                </div>
              </div>

              {/* Details — template-driven (Settings → Templates → Technician Approval Page) */}
              {jobData?.approval_page_text ? (
                <div className="p-4 space-y-1" data-testid="approval-page-details">
                  {jobData.approval_page_text.split("\n").map((line, idx) => renderTemplateLine(line, idx))}
                  {getGoogleMapsUrl(job) && (
                    <div className="flex items-center gap-2 pt-3">
                      <a href={getGoogleMapsUrl(job)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                        data-testid="approval-google-maps-btn">
                        <Navigation className="w-3 h-3" /> Google Maps <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                      <a href={`https://maps.apple.com/?q=${encodeURIComponent(getDisplayAddress(job))}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 transition-colors"
                        data-testid="approval-apple-maps-btn">
                        <Navigation className="w-3 h-3" /> Apple Maps <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  )}
                </div>
              ) : (
              <div className="p-4 space-y-3">
                {/* Customer Name */}
                {job.customer_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'rgba(59,130,246,0.1)'}}>
                      <User className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/40 text-[11px]">Customer</p>
                      <p className="text-white text-sm">{job.customer_name}</p>
                    </div>
                  </div>
                )}

                {/* Customer Phone */}
                {(job.masked_call_number_with_pin || job.customer_phone) && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'rgba(34,197,94,0.1)'}}>
                      <Phone className="w-4 h-4 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/40 text-[11px]">{job.masked_call_number_with_pin ? "Masked Call" : "Phone"}</p>
                      <a href={`tel:${job.masked_call_number_with_pin ? job.masked_call_number_with_pin.replace(/[^0-9,#]/g, '') : job.customer_phone}`} className="text-green-400 text-sm underline decoration-green-400/30">
                        {job.masked_call_number_with_pin || job.customer_phone}
                      </a>
                    </div>
                  </div>
                )}

                {/* Address */}
                {job.customer_address && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'rgba(249,115,22,0.1)'}}>
                        <MapPin className="w-4 h-4 text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/40 text-[11px]">Address</p>
                        <p className="text-white text-sm">{getDisplayAddress(job)}</p>
                      </div>
                    </div>
                    {getGoogleMapsUrl(job) && (
                      <div className="flex items-center gap-2 pl-11">
                        <a href={getGoogleMapsUrl(job)} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                          data-testid="approval-google-maps-btn">
                          <Navigation className="w-3 h-3" /> Google Maps <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                        <a href={`https://maps.apple.com/?q=${encodeURIComponent(getDisplayAddress(job))}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 transition-colors"
                          data-testid="approval-apple-maps-btn">
                          <Navigation className="w-3 h-3" /> Apple Maps <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Schedule */}
                {job.scheduled_date && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'rgba(139,92,246,0.1)'}}>
                      <Calendar className="w-4 h-4 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-white/40 text-[11px]">Date & Time</p>
                      <p className="text-white text-sm">
                        {formatDateWithDay(job.scheduled_date)}
                        {(formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date)) && <span className="text-white/50 ml-2">at {formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date)}</span>}
                      </p>
                    </div>
                  </div>
                )}

                {/* Job Type */}
                {job.service_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'rgba(255,204,0,0.1)'}}>
                      <Briefcase className="w-4 h-4 text-[#FFCC00]" />
                    </div>
                    <div>
                      <p className="text-white/40 text-[11px]">Job Type</p>
                      <p className="text-white text-sm">{job.service_name}</p>
                    </div>
                  </div>
                )}

                {/* Vehicle */}
                {(job.vehicle_make || job.vehicle_model || job.vehicle_year) && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'rgba(236,72,153,0.1)'}}>
                      <Car className="w-4 h-4 text-pink-400" />
                    </div>
                    <div>
                      <p className="text-white/40 text-[11px]">Vehicle</p>
                      <p className="text-white text-sm">
                        {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {job.notes && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{background: 'rgba(255,255,255,0.04)'}}>
                      <FileText className="w-4 h-4 text-white/40" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/40 text-[11px]">Notes</p>
                      <p className="text-white/70 text-sm whitespace-pre-wrap">{job.notes}</p>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2.5">
            {job && (
              <Button
                onClick={() => copyToClipboard(jobData?.copy_ticket_text || buildTicketText(job, businessName), "ticket")}
                className="w-full h-12 rounded-xl text-sm font-medium"
                style={{background: 'rgba(255,204,0,0.1)', color: '#FFCC00', border: '1px solid rgba(255,204,0,0.25)'}}
                data-testid="copy-ticket-btn"
              >
                {copiedField === "ticket" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copiedField === "ticket" ? "Copied!" : "Copy Full Ticket"}
              </Button>
            )}
            <Button 
              onClick={() => window.location.href = "/tech/jobs"}
              className="w-full h-12 rounded-xl text-sm font-medium bg-[#0046BE] text-white hover:bg-[#0046BE]/80"
              data-testid="go-to-jobs-btn"
            >
              Go to My Jobs
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Already responded - Rejected
  if (responded && responseAction === "rejected") {
    return (
      <div className="min-h-screen bg-[#06060b] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)'}}>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white font-['Manrope'] mb-2">Job Rejected</h2>
          <p className="text-white/50 text-sm">
            This job has been unassigned. Your employer has been notified.
          </p>
        </div>
      </div>
    );
  }

  // No job data (invalid/expired)
  if (!jobData) {
    return (
      <div className="min-h-screen bg-[#06060b] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)'}}>
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white font-['Manrope'] mb-2">Link Expired</h2>
          <p className="text-white/50 text-sm">
            This job approval link is no longer valid. Please contact your employer.
          </p>
        </div>
      </div>
    );
  }

  const { job, business_name } = jobData;

  return (
    <div className="min-h-screen bg-[#06060b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="text-xl font-bold text-white font-['Manrope']">New Job Assignment</h1>
          <p className="text-white/40 text-sm mt-0.5">{business_name}</p>
        </div>

        {/* Job Card */}
        <div className="rounded-2xl overflow-hidden mb-5" style={{background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)'}}>
          {/* Title */}
          <div className="p-4 border-b border-white/[0.06]">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">{job.title}</h3>
              {job.priority === "high" && (
                <Badge className="bg-red-500/15 text-red-400 border border-red-500/25 text-xs">Urgent</Badge>
              )}
            </div>
            {job.job_display_id && (
              <div className="flex items-center gap-2 mt-1.5">
                <Hash className="w-3.5 h-3.5 text-white/30" />
                <span className="text-white/40 text-sm font-mono">{job.job_display_id}</span>
              </div>
            )}
          </div>

          <div className="p-4 space-y-3">
            {/* Vehicle Information */}
            {(job.vehicle_make || job.vehicle_model || job.vehicle_year) && (
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)'}}>
                <Car className="w-5 h-5 text-orange-400" />
                <span className="text-white font-medium text-sm">
                  {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")}
                </span>
              </div>
            )}

            {job.service_name && (
              <div className="flex items-center gap-3">
                <Briefcase className="w-4 h-4 text-[#FFCC00]" />
                <span className="text-white/70 text-sm">{job.service_name}</span>
              </div>
            )}
            
            {job.scheduled_date && (
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span className="text-white/70 text-sm">{formatDateWithDay(job.scheduled_date)}</span>
              </div>
            )}
            
            {(formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date)) && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-violet-400" />
                <span className="text-white/70 text-sm">{formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date)}</span>
              </div>
            )}
            
            {(job.address_city || job.city || job.zip_code) && (
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-green-400" />
                <span className="text-white/70 text-sm">
                  {[job.address_city || job.city, job.address_state, job.zip_code].filter(Boolean).join(", ")}
                </span>
              </div>
            )}

            {job.notes && (
              <div className="mt-2 p-3 rounded-lg" style={{background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)'}}>
                <p className="text-white/60 text-sm">{job.notes}</p>
              </div>
            )}

            <div className="pt-3 border-t border-white/[0.06]">
              <p className="text-white/30 text-xs text-center">
                Accept to view full customer details and address
              </p>
            </div>
          </div>
        </div>

        {/* Rejection Form */}
        {showRejectForm && (
          <div className="rounded-2xl p-4 mb-5" style={{background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)'}}>
            <p className="text-white/60 text-sm mb-3">Why are you rejecting this job? (optional)</p>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Schedule conflict, too far away, etc."
              className="bg-white/[0.03] border-white/10 text-white placeholder:text-white/20 mb-3"
              data-testid="rejection-reason-input"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => setShowRejectForm(false)}
                className="flex-1 h-10 rounded-xl text-sm"
                style={{background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)'}}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleResponse("reject")}
                disabled={submitting}
                className="flex-1 h-10 rounded-xl text-sm bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-reject-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Reject"}
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!showRejectForm && (
          <div className="flex gap-3">
            <Button
              onClick={() => handleResponse("reject")}
              disabled={submitting}
              className="flex-1 h-12 rounded-xl text-sm font-medium"
              style={{background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)'}}
              data-testid="reject-btn"
            >
              <XCircle className="w-5 h-5 mr-2" />
              Reject
            </Button>
            <Button
              onClick={() => handleResponse("accept")}
              disabled={submitting}
              className="flex-1 h-12 rounded-xl text-sm font-medium bg-green-600 hover:bg-green-700 text-white"
              data-testid="accept-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Accept Job
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApproval;
